// src/portal-app/shell/kit/ShareBar.js
//
// A proportion shown as a bar, replacing strips like "Cash £773.64 · Card
// £474.16" where the reader had to do the division themselves.
//
// Accessibility: colour never carries the meaning alone — every segment has a
// legend entry with its label and value alongside the dot.
import React from 'react';
import { ensureKitCss } from './kitCss';
import { toneVar } from './tones';

export function ShareBar({ segments, ariaLabel }) {
    ensureKitCss();
    if (!Array.isArray(segments) || segments.length === 0) return null;

    // Verified defect (spec §12a): total used to sum EVERY segment,
    // including negatives, then divide only the positive ones by that
    // inflated figure — one negative segment pushed every positive
    // segment's width past its true share and the bar could overflow
    // 100%. ShareBar replaces the P&L strip on Cost Management (Task 13),
    // where a loss is a legitimate negative, so `total` is now the sum of
    // positive segments only — rendered widths always sum to 100%. A
    // negative segment is excluded from the bar but stays in `segments`
    // below, so the legend still shows its real value.
    const positive = segments.filter((s) => (Number(s.value) || 0) > 0);
    const total = positive.reduce((sum, s) => sum + (Number(s.value) || 0), 0);
    // A zero total (no positive segments) would make every width NaN%;
    // render the empty track and let the legend carry the figures.
    const bars = total > 0 ? positive : [];

    return (
        <div className="pk-share">
            <div
                className="pk-share-track"
                role={ariaLabel ? 'img' : undefined}
                aria-label={ariaLabel || undefined}
                aria-hidden={ariaLabel ? undefined : 'true'}
            >
                {bars.map((s) => {
                    const token = toneVar(s.tone);
                    const pct = ((Number(s.value) || 0) / total) * 100;
                    return (
                        <span
                            key={s.id}
                            className="pk-share-seg"
                            style={{ width: `${pct}%`, background: `var(${token})` }}
                        />
                    );
                })}
            </div>
            <div className="pk-share-leg">
                {segments.map((s) => {
                    const token = toneVar(s.tone);
                    return (
                        <span className="pk-share-leg-item" key={s.id}>
                            <span
                                className="pk-share-dot"
                                style={{ background: `var(${token})` }}
                                aria-hidden="true"
                            />
                            <span className="pk-share-leg-label">{s.label}</span>
                            <span className="pk-share-leg-value pk-num">{s.display}</span>
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

export default ShareBar;
